import React, { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import useTeacherClassScope from '../hooks/useTeacherClassScope';
import { getTaskStorageUrl } from '../utils/imageUtils';

const TITLE_MAX = 150;
const MESSAGE_MAX = 500;

export default function NotificationList() {
    const { http, user } = AuthUser();
    const scope = useTeacherClassScope(http, user);
    const [notifications, setNotifications] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [audience, setAudience] = useState('class'); // class | group
    const [groupId, setGroupId] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        message: '',
    });

    useEffect(() => {
        fetchNotifications();
        fetchGroups();
    }, []);

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await http.get(`/notifications?company_id=${user.company_id}`);
            if (response.data.success) {
                setNotifications(response.data.data || []);
            } else {
                toast.error(response.data.message || 'Failed to load notifications');
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
            toast.error(error.response?.data?.message || 'Error fetching notifications');
        } finally {
            setLoading(false);
        }
    };

    const fetchGroups = async () => {
        try {
            const response = await http.get(`/broadcast_groups?company_id=${user.company_id}`);
            if (response.data.success) {
                setGroups(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching groups:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (audience === 'class') {
            if (!scope.canSelectClassDivision) {
                toast.error('No class/division assigned to you');
                return;
            }
            if (!scope.selectedClass) {
                toast.error('Please select class');
                return;
            }
            if (!scope.selectedDivision) {
                toast.error('Please select division');
                return;
            }
        } else if (!groupId) {
            toast.error('Please select a broadcast group');
            return;
        }

        if (!formData.title.trim()) {
            toast.error('Title is required');
            return;
        }
        if (!formData.message.trim()) {
            toast.error('Message is required');
            return;
        }
        if (formData.title.length > TITLE_MAX) {
            toast.error(`Title must be ${TITLE_MAX} characters or less`);
            return;
        }
        if (formData.message.length > MESSAGE_MAX) {
            toast.error(`Message must be ${MESSAGE_MAX} characters or less`);
            return;
        }

        setSending(true);
        try {
            const companyId = user?.company_id;
            if (!companyId) {
                toast.error('Company not found. Please login again.');
                setSending(false);
                return;
            }

            const body = new FormData();
            body.append('company_id', String(companyId));
            body.append('title', formData.title.trim());
            body.append('message', formData.message.trim());
            body.append('login_user', String(user.id));
            if (audience === 'group') {
                body.append('group_id', String(groupId));
            } else {
                body.append('class', scope.selectedClass);
                body.append('division', scope.selectedDivision);
            }
            if (imageFile) {
                body.append('image', imageFile);
            }

            // AuthUser defaults Content-type: application/json — false clears it so
            // the browser can set multipart/form-data with boundary
            const response = await http.post(
                `/notifications?company_id=${companyId}`,
                body,
                {
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': false,
                        'Content-type': false,
                    },
                }
            );

            if (response.data.success) {
                toast.success(response.data.message || 'Notification sent!');
                setFormData({ title: '', message: '' });
                setImageFile(null);
                setGroupId('');
                scope.resetClassDivisionAfterSend();
                fetchNotifications();
            } else {
                toast.error(response.data.message || 'Failed to send');
            }
        } catch (error) {
            console.error('Error sending notification:', error);
            toast.error(error.response?.data?.message || 'Error sending notification');
        } finally {
            setSending(false);
        }
    };

    const formatDate = (value) => {
        if (!value) return '—';
        try {
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) return value;
            return d.toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return value;
        }
    };

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-4 px-4">
                <div className="row mb-4">
                    <div className="col-12">
                        <h3 className="mb-1">Notifications</h3>
                        <p className="text-muted mb-0">
                            Post a notification to a class/division or broadcast group. Optional image shows in the mobile app.
                        </p>
                    </div>
                </div>

                <div className="card mb-4">
                    <div className="card-header">
                        <h5 className="mb-0">Send Notification</h5>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="form-label">Send to</label>
                                    <select
                                        className="form-control"
                                        value={audience}
                                        onChange={(e) => {
                                            setAudience(e.target.value);
                                            setGroupId('');
                                        }}
                                    >
                                        <option value="class">Class + Division</option>
                                        <option value="group">Broadcast Group</option>
                                    </select>
                                </div>

                                {audience === 'class' ? (
                                    <>
                                        <div className="col-md-4">
                                            <label className="form-label">Class</label>
                                            <select
                                                className="form-control"
                                                value={scope.selectedClass}
                                                onChange={(e) => scope.onClassChange(e.target.value)}
                                                disabled={scope.loadingScope || scope.fieldsLocked}
                                            >
                                                <option value="">Select Class</option>
                                                {scope.classOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label">Division</label>
                                            <select
                                                className="form-control"
                                                value={scope.selectedDivision}
                                                onChange={(e) => scope.onDivisionChange(e.target.value)}
                                                disabled={scope.loadingScope || scope.fieldsLocked}
                                            >
                                                <option value="">Select Division</option>
                                                {scope.divisionOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </>
                                ) : (
                                    <div className="col-md-8">
                                        <label className="form-label">Broadcast Group</label>
                                        <select
                                            className="form-control"
                                            value={groupId}
                                            onChange={(e) => setGroupId(e.target.value)}
                                        >
                                            <option value="">Select Group</option>
                                            {groups.map((g) => (
                                                <option key={g.id} value={g.id}>
                                                    {g.name} ({g.item_count} class/div)
                                                </option>
                                            ))}
                                        </select>
                                        {groups.length === 0 && (
                                            <small className="text-muted">
                                                Create groups under Mobile App → Broadcast Groups.
                                            </small>
                                        )}
                                    </div>
                                )}

                                <div className="col-md-8">
                                    <label className="form-label">
                                        Title{' '}
                                        <span className="text-muted">
                                            ({formData.title.length}/{TITLE_MAX})
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        className="form-control"
                                        value={formData.title}
                                        onChange={handleChange}
                                        maxLength={TITLE_MAX}
                                        placeholder="Notification title"
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Image (optional)</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label">
                                        Message{' '}
                                        <span className="text-muted">
                                            ({formData.message.length}/{MESSAGE_MAX})
                                        </span>
                                    </label>
                                    <textarea
                                        name="message"
                                        className="form-control"
                                        rows={3}
                                        value={formData.message}
                                        onChange={handleChange}
                                        maxLength={MESSAGE_MAX}
                                        placeholder="Write the notification message..."
                                    />
                                </div>
                                <div className="col-12">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={sending}
                                    >
                                        {sending ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-paper-plane me-1"></i> Send
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Sent Notifications</h5>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={fetchNotifications}
                            disabled={loading}
                        >
                            <i className="fa fa-refresh me-1"></i> Refresh
                        </button>
                    </div>
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="text-center py-4">
                                <p className="text-muted mb-0">
                                    No notifications sent yet. Use the form above to send one.
                                </p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 50 }}>#</th>
                                            <th>Date</th>
                                            <th>Class</th>
                                            <th>Division</th>
                                            <th>Title</th>
                                            <th>Message</th>
                                            <th>Image</th>
                                            <th>Sent By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {notifications.map((item, index) => (
                                            <tr key={item.id}>
                                                <td>{index + 1}</td>
                                                <td className="text-nowrap">
                                                    {formatDate(item.created_at)}
                                                </td>
                                                <td>{item.class}</td>
                                                <td>{item.division}</td>
                                                <td>
                                                    <strong>{item.title}</strong>
                                                </td>
                                                <td style={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>
                                                    {item.message}
                                                </td>
                                                <td>
                                                    {item.image ? (
                                                        <a
                                                            href={getTaskStorageUrl(item.image)}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            <img
                                                                src={getTaskStorageUrl(item.image)}
                                                                alt=""
                                                                style={{
                                                                    width: 48,
                                                                    height: 48,
                                                                    objectFit: 'cover',
                                                                    borderRadius: 6,
                                                                }}
                                                            />
                                                        </a>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td>{item.sent_by || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
